import { captureException, isDev } from "./init";
import { sanitizeIds } from "./sanitizeIds";
import type { EnrichedError } from "./utils";

/**
 * Redact query parameter values from a URL, keeping only the keys.
 * e.g. "/api/users?token=secret&page=2" → "/api/users?token=<redacted>&page=<redacted>"
 */
function redactUrl(url: string): string {
  try {
    const base = typeof document !== "undefined" ? document.location.href : "https://example.com";
    const parsed = new URL(url, base);
    for (const key of parsed.searchParams.keys()) {
      parsed.searchParams.set(key, "<redacted>");
    }
    return parsed.toString();
  } catch (e) {
    if (e instanceof TypeError || e instanceof URIError) {
      // If URL parsing fails, strip everything after '?'
      return url.split("?")[0];
    }
    throw e;
  }
}

/**
 * Error class for capturing bare fetch failures to Sentry.
 * This error is used for telemetry only - the original error is rethrown.
 */
export class BareFetchError extends Error implements EnrichedError {
  cause: Error;
  tags: Record<string, string>;
  extra: Record<string, unknown>;
  contexts: Record<string, Record<string, unknown>>;
  fingerprint: string[];

  constructor(originalError: Error, url: string, init?: RequestInit) {
    super(`Fetch failed: ${redactUrl(url)}`);
    this.name = "BareFetchError";
    this.cause = originalError;

    const method = init?.method ?? "GET";

    // Parse URL for context
    let parsedUrl: URL | null = null;
    try {
      parsedUrl = new URL(url, typeof document !== "undefined" ? document.location.href : "https://example.com");
    } catch (e) {
      // Invalid URL, continue without parsed info
      if (e instanceof URIError || e instanceof TypeError) {
        // Expected URL parsing errors - continue without parsed info
      } else {
        throw e;
      }
    }

    this.tags = {
      errorType: "bare_fetch_error",
      method,
      ...(parsedUrl && { host: parsedUrl.host }),
    };

    this.extra = {
      originalErrorName: originalError.name,
      originalErrorMessage: originalError.message,
    };

    this.contexts = {
      request: {
        url: parsedUrl ? `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}` : url.split("?")[0],
        method,
        ...(parsedUrl && {
          host: parsedUrl.host,
          pathname: parsedUrl.pathname,
          searchParams: [...parsedUrl.searchParams.keys()],
        }),
      },
    };

    // "{{ default }}" keeps Sentry's default grouping components (stack trace included) in the
    // hash instead of replacing them: the caller identity of a bare fetch lives deep in the
    // stack, since every BareFetchError is constructed at the same line of the fetch patch.
    // Pathname is added because host alone collapses unrelated asset failures on one host into
    // a single issue; sanitizeIds keeps ids in the path from splitting one issue per id.
    this.fingerprint = [
      "{{ default }}",
      "bare-fetch-error",
      method,
      parsedUrl?.host ?? "unknown",
      parsedUrl ? sanitizeIds(parsedUrl.pathname) : "unknown-path",
    ];
  }
}

/**
 * The original, unpatched fetch function. Use this in code that already handles
 * its own error reporting (e.g., dojo-fetch) to avoid double-reporting errors
 * through both the fetch patch and the caller's own error handling.
 */
export const originalFetch: typeof fetch =
  typeof window !== "undefined" && typeof window.fetch === "function" ? window.fetch.bind(window) : fetch;

/**
 * Symbol placed on the RequestInit of every dojoFetch call. patchedFetch skips
 * BareFetchError for requests carrying this marker, avoiding double-reporting —
 * dojo-fetch already converts network failures into NoNetworkError.
 *
 * Uses Symbol.for() so both patchFetch and dojo-fetch resolve the same symbol
 * from the global registry without importing it from each other — test mocks of
 * @web-monorepo/telemetry never need to export this.
 */
export const DOJO_FETCH_MARKER = Symbol.for("dojo.fetch.handled");

/**
 * Set once the page starts unloading. Every in-flight fetch is torn down at that point,
 * and the resulting rejection says nothing about the app — Safari surfaces those
 * cancellations as generic `TypeError`s (notably for Vite's modulepreload fallback fetch
 * of a lazy route chunk, which Safari has to make because it lacks `modulepreload`)
 * rather than swallowing them the way Chrome does.
 *
 * `pagehide` rather than the deprecated `beforeunload`: it fires reliably in Safari on
 * both a real unload and a BFCache navigation, before requests are cancelled.
 *
 * A BFCache navigation is not terminal, though: the heap is frozen and thawed, so this
 * module survives into the restored page with the flag still set. `pageshow` with
 * `persisted` clears it, or the restored page would report nothing for the rest of its
 * life. A non-persisted `pageshow` is an ordinary load, where the flag is already false.
 */
let isUnloading = false;

if (typeof window !== "undefined") {
  window.addEventListener(
    "pagehide",
    () => {
      isUnloading = true;
    },
    { capture: true },
  );

  window.addEventListener(
    "pageshow",
    (event) => {
      if (event.persisted) {
        isUnloading = false;
      }
    },
    { capture: true },
  );
}

// Monkey-patch fetch if in browser environment (skip in dev/test environments)
if (typeof window !== "undefined" && typeof window.fetch === "function" && !isDev) {
  const patchedFetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    try {
      return await originalFetch(input, init);
    } catch (error) {
      const handledByDojoFetch = Boolean((init as Record<symbol, unknown> | undefined)?.[DOJO_FETCH_MARKER]);
      // Unload-cancelled fetches are reported to nobody, but the error still has to reach
      // the caller: only the telemetry side-channel is suppressed.
      if (error instanceof Error && !handledByDojoFetch && !isUnloading) {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
        const bareFetchError = new BareFetchError(error, url, init);
        captureException(bareFetchError);
      }
      throw error;
    }
  };

  window.fetch = patchedFetch;
}
